import { useState, useEffect, useCallback, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface PolygonData {
  coordinates: number[][][]; // GeoJSON-like: [[[lng, lat], ...]]
}

interface PolygonDrawerProps {
  value: PolygonData | null;
  onChange: (polygon: PolygonData | null) => void;
}

export function PolygonDrawer({ value, onChange }: PolygonDrawerProps) {
  const map = useMap();
  const [vertices, setVertices] = useState<google.maps.LatLngLiteral[]>([]);
  const [isClosed, setIsClosed] = useState(false);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const internalUpdateRef = useRef(false);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Convert GeoJSON polygon to vertices
  const geoJsonToVertices = useCallback((data: PolygonData): google.maps.LatLngLiteral[] => {
    const ring = data.coordinates[0] ?? [];
    // GeoJSON rings repeat first vertex at end — skip it
    const verts = ring.slice(0, -1).map((coord) => ({ lat: coord[1]!, lng: coord[0]! }));
    return verts;
  }, []);

  // Convert vertices to GeoJSON polygon
  const verticesToGeoJson = useCallback((verts: google.maps.LatLngLiteral[]): PolygonData => {
    const ring = verts.map((v) => [v.lng, v.lat]);
    ring.push(ring[0]!); // Close the ring
    return { coordinates: [ring] };
  }, []);

  // Clean up all map overlays
  const cleanup = useCallback(() => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    for (const marker of markersRef.current) {
      marker.map = null;
    }
    markersRef.current = [];
    if (clickListenerRef.current) {
      google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
  }, []);

  // Create a draggable vertex marker
  const createVertexMarker = useCallback((
    position: google.maps.LatLngLiteral,
    index: number,
    isFirst: boolean,
  ): google.maps.marker.AdvancedMarkerElement | null => {
    if (!map) return null;

    const div = document.createElement("div");
    div.style.width = isFirst ? "14px" : "10px";
    div.style.height = isFirst ? "14px" : "10px";
    div.style.borderRadius = "50%";
    div.style.backgroundColor = isFirst ? "#22c55e" : "#3b82f6";
    div.style.border = "2px solid white";
    div.style.cursor = "pointer";
    div.style.boxShadow = "0 1px 3px rgba(0,0,0,0.3)";

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      map,
      content: div,
      gmpDraggable: true,
      zIndex: isFirst ? 100 : 50,
    });

    // Handle drag for vertex editing
    marker.addListener("dragend", () => {
      const pos = marker.position;
      if (!pos) return;
      setVertices((prev) => {
        const updated = [...prev];
        updated[index] = { lat: pos.lat as number, lng: pos.lng as number };
        return updated;
      });
    });

    // Click on first vertex to close polygon
    if (isFirst) {
      marker.addListener("gmp-click", () => {
        setVertices((prev) => {
          if (prev.length >= 3) {
            setIsClosed(true);
          }
          return prev;
        });
      });
    }

    return marker;
  }, [map]);

  // Draw/update the polygon and markers on the map
  const renderPolygon = useCallback((verts: google.maps.LatLngLiteral[], closed: boolean) => {
    if (!map) return;

    // Clean previous overlays
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    for (const m of markersRef.current) {
      m.map = null;
    }
    markersRef.current = [];

    if (verts.length === 0) return;

    if (closed && verts.length >= 3) {
      // Draw filled polygon
      polygonRef.current = new google.maps.Polygon({
        paths: verts,
        fillColor: "#3b82f6",
        fillOpacity: 0.15,
        strokeColor: "#3b82f6",
        strokeWeight: 2,
        strokeOpacity: 0.8,
        map,
      });
    } else if (verts.length >= 2) {
      // Draw polyline while drawing
      polylineRef.current = new google.maps.Polyline({
        path: verts,
        strokeColor: "#3b82f6",
        strokeWeight: 2,
        strokeOpacity: 0.8,
        map,
      });
    }

    // Add vertex markers
    for (let i = 0; i < verts.length; i++) {
      const marker = createVertexMarker(verts[i]!, i, i === 0 && !closed);
      if (marker) markersRef.current.push(marker);
    }
  }, [map, createVertexMarker]);

  // Initialize from value prop (skip if we triggered the change ourselves)
  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }
    if (value && map) {
      const verts = geoJsonToVertices(value);
      setVertices(verts);
      setIsClosed(true);
    }
  }, [value, map, geoJsonToVertices]);

  // Re-render polygon when vertices change
  useEffect(() => {
    renderPolygon(vertices, isClosed);

    if (isClosed && vertices.length >= 3) {
      internalUpdateRef.current = true;
      onChangeRef.current(verticesToGeoJson(vertices));
    }
  }, [vertices, isClosed, renderPolygon, verticesToGeoJson]);

  // Set up click listener for drawing
  useEffect(() => {
    if (!map || isClosed) {
      if (clickListenerRef.current) {
        google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      return;
    }

    // Set crosshair cursor during drawing
    map.setOptions({ draggableCursor: "crosshair" });

    clickListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const newPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setVertices((prev) => [...prev, newPoint]);
    });

    return () => {
      if (clickListenerRef.current) {
        google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      map.setOptions({ draggableCursor: null });
    };
  }, [map, isClosed]);

  // Handle double-click to close polygon
  useEffect(() => {
    if (!map || isClosed) return;

    const dblClickListener = map.addListener("dblclick", (e: google.maps.MapMouseEvent) => {
      e.stop();
      if (vertices.length >= 3) {
        setIsClosed(true);
      }
    });

    return () => {
      google.maps.event.removeListener(dblClickListener);
    };
  }, [map, isClosed, vertices.length]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleClear = () => {
    cleanup();
    setVertices([]);
    setIsClosed(false);
    onChange(null);
  };

  return (
    <div className="absolute bottom-3 left-3 z-10">
      {!isClosed && vertices.length === 0 && (
        <div className="rounded-lg bg-card/90 backdrop-blur px-3 py-2 shadow-md text-xs text-muted-foreground">
          Click on the map to draw a polygon. Click first vertex or double-click to close.
        </div>
      )}
      {!isClosed && vertices.length > 0 && (
        <div className="rounded-lg bg-card/90 backdrop-blur px-3 py-2 shadow-md text-xs text-muted-foreground flex items-center gap-2">
          <span>{vertices.length} vertices. Click first vertex (green) or double-click to close.</span>
          <Button type="button" size="sm" variant="ghost" className="h-6 px-2" onClick={handleClear}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
      {isClosed && (
        <div className="rounded-lg bg-card/90 backdrop-blur px-3 py-2 shadow-md text-xs flex items-center gap-2">
          <span className="text-muted-foreground">Polygon: {vertices.length} vertices. Drag to edit.</span>
          <Button type="button" size="sm" variant="ghost" className="h-6 px-2" onClick={handleClear}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
